import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import Typography from '@mui/material/Typography';
import { useUploadCompanyMediaLogosMutation } from '../../../graphql/mutation/company/companyMediaLogo/uploadCompanyMediaLogosMutation';
import { useUnpublishCompanyMediaLogosMutation } from '../../../graphql/mutation/company/companyMediaLogo/unpublishCompanyMediaLogosMutation';
import { useDeleteCompanyMediaLogosMutation } from '../../../graphql/mutation/company/companyMediaLogo/deleteCompanyMediaLogosMutation';
import { usePublishCompanyMediaLogosMutation } from '../../../graphql/mutation/company/companyMediaLogo/publishCompanyMediaLogosMutation';
import BaseImageUpload from './_module/BaseImageUpload';
import useNotification from '../_hook/useNotification';

interface IProps {
  companyId: string;
  logos: {
    status: boolean;
    filename: string;
    filename_large?: string;

    filenameUrl?: string;
    filenameLargeUrl?: string;
  }[];
}

export default function CompanyLogoUpload(props: IProps) {
  const { companyId, logos } = props;
  const [uploadingCount, setUploadingCount] = useState(0);
  const { t } = useTranslation();
  const { notification } = useNotification();
  const queryFragment = `updatedCompany {
				_id
                media {
                    images {
                      logo {
                        status
                        filename
                        filename_large
                        
                        filenameUrl
                        filenameLargeUrl
                      }
                    }
                }
			}`;
  const [uploadCompanyMediaLogosMutation] = useUploadCompanyMediaLogosMutation(
    queryFragment,
    {
      onCompleted: () => {
        setUploadingCount(0);
      },
      onError: (e) => {
        setUploadingCount(0);
        notification.error(e.message);
      },
    }
  );
  const [deleteCompanyMediaLogosMutation] = useDeleteCompanyMediaLogosMutation(
    queryFragment,
    {
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );
  const [
    publishCompanyMediaLogosMutation,
  ] = usePublishCompanyMediaLogosMutation(queryFragment, {
    onError: (e) => {
      notification.error(e.message);
    },
  });
  const [
    unpublishCompanyMediaLogosMutation,
  ] = useUnpublishCompanyMediaLogosMutation(queryFragment, {
    onError: (e) => {
      notification.error(e.message);
    },
  });
  // 200KB
  const maximumUploadSize = 204800;
  const acceptedUploadFileTypes = 'image/png, image/jpeg, image/jpg';

  function onUpload(fileList: FileList | null) {
    if (fileList && fileList.length) {
      setUploadingCount(uploadingCount + fileList.length);
      uploadCompanyMediaLogosMutation({
        variables: {
          _id: companyId,
          logos: fileList,
        },
      });
    }
  }

  return (
    <>
      <Typography variant="h6">
        {t('upload')} {t('logo')}
      </Typography>
      <BaseImageUpload
        images={logos}
        color="primary"
        maximumUploadSize={maximumUploadSize}
        displayImageSize="large"
        uploadInfo={{
          ratio: '1 : 1',
          size: '200KB',
          acceptedUploadFileTypes,
        }}
        uploadingCount={uploadingCount}
        onUpload={onUpload}
        onDelete={(filename) => {
          deleteCompanyMediaLogosMutation({
            variables: {
              _id: companyId,
              logoFilenames: [filename],
            },
          });
        }}
        onPublish={(filenames) => {
          publishCompanyMediaLogosMutation({
            variables: {
              _id: companyId,
              filenames,
            },
          });
        }}
        onUnpublish={(filenames) => {
          unpublishCompanyMediaLogosMutation({
            variables: {
              _id: companyId,
              filenames,
            },
          });
        }}
      />
    </>
  );
}
